import {Component, Element, Event, EventEmitter, h, Host, Method, Prop} from '@stencil/core'
import {CardClass, CardDirectionType} from './constants'
import {booleanString, govHost} from '../../helpers/Dom/template'
import {GovHost} from '../../helpers/Dom/template.types'
import {createID} from '../../utils/string.utils'
import {validateAccessibleLabel} from '../../helpers/Validate/wcag'
import {canValidateWcagOnRender} from '../../helpers/Dom/win'
import {delay} from '../../utils/utils'
import {GovCardChangeEvent} from './interfaces'
import {ButtonTargetType} from "../gov-button/button/constants";

enum Tags {
	A = "a",
	DIV = "div",
}

@Component({
	tag: 'gov-card',
})
export class GovCard {
	private h: GovHost
	private readonly headlineId: string
	private readonly contentId: string
	private hydrated = false

	constructor() {
		this.h = govHost(this.host)
		this.headlineId = createID('GovCardHeadline')
		this.contentId = createID('GovCardContent')
	}

	@Element() host: HTMLGovCardElement

	/**
	 * Card direction
	 */
	@Prop() readonly direction: CardDirectionType = "vertical"
	/**
	 * Defines weather the card can be collapsible
	 */
	@Prop() readonly collapsible: boolean = false
	/**
	 * Defines weather the card can be clickable with hover effect
	 */
	@Prop() readonly clickable: boolean = false
	/**
	 * Defines whether the card is open or closed
	 */
	@Prop({reflect: true, mutable: true}) open = true
	/**
	 * Link on whole card
	 */
	@Prop({reflect: true}) readonly href: string
	/**
	 * Same as original parameter
	 * @link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
	 */
	@Prop() readonly target?: ButtonTargetType
	/**
	 * Adds accessible label for the collapsible button that is only shown for screen readers.
	 */
	@Prop({attribute: 'accessible-label'}) accessibleTriggerLabel: string
	/**
	 * A string of identifiers that indicate alternative label elements for collapsible card
	 */
	@Prop({attribute: 'accessible-labelledby'}) accessibleTriggerLabelledBy: string
	/**
	 * Called when the card toggle state changes
	 */
	@Event({eventName: 'gov-toggle'}) govToggle: EventEmitter<GovCardChangeEvent>

	async componentDidRender() {
		if (canValidateWcagOnRender()) {
			await delay(500)
			await this.validateAccessibility()
		}
	}

	connectedCallback() {
		this.hydrated = true
	}

	render() {
		const Tag = this.href ? Tags.A : Tags.DIV

		return (
			<Host class={this.h.classes(CardClass.root)} open={this.open}
				  collapsible={booleanString(this.collapsible)}
				  clickable={this.clickable}
				  direction={this.direction} hydrated={this.hydrated}>
				{this.collapsible ? (
					<button
						id={this.headlineId}
						class={CardClass.header}
						aria-label={this.accessibleTriggerLabel}
						aria-labelledby={this.accessibleTriggerLabelledBy}
						aria-expanded={booleanString(this.open)}
						aria-controls={this.contentId}
						onClick={this.onClickHandler.bind(this)}
					>
						<slot name="headline"/>
						<gov-icon class={CardClass.arrow} name="chevron-down" type="components"></gov-icon>
					</button>
				) : this.h.hasSlot("headline") ? (
					<div class={CardClass.header}>
						<slot name="headline"/>
					</div>
				) : null}

				<Tag href={this.href}
					 target={this.target}
					 id={this.contentId}
					 aria-hidden={this.collapsible ? booleanString(!this.open) : undefined}
					 hidden={this.collapsible ? !this.open : undefined}
					 aria-labelledby={this.collapsible ? this.headlineId : undefined}
					 class={CardClass.inner}>

					{this.h.hasSlot('img') && (
						<div class={CardClass.img}>
							<slot name="img"/>
						</div>
					)}

					{this.h.hasSlot('icon') && (
						<div class={CardClass.icon}>
							<slot name="icon"/>
						</div>
					)}

					<div class={CardClass.content}>
						<slot/>
					</div>

					{this.h.hasSlot('footer') && (
						<div class={CardClass.footer}>
							<slot name="footer"/>
						</div>
					)}
				</Tag>
			</Host>
		)
	}

	private onClickHandler(e) {
		e.stopPropagation()
		if (this.collapsible) {
			this.open = !this.open
			this.govToggle.emit({open: this.open, element: this.host})
		}
	}

	/**
	 * Validate the accessible attributes of the component
	 */
	@Method()
	async validateAccessibility(): Promise<void> {
		if (this.collapsible) {
			validateAccessibleLabel(this.accessibleTriggerLabel, this.accessibleTriggerLabelledBy, CardClass.root, this.host)
		}
	}
}
